import React, { useState, MouseEvent } from 'react';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import FormInput from '../../common/FormInput/FormInput'; 
import { DefaultConfig } from '../../default-config';
import Constant from '../../static/data/Constant';
import { generateId } from '../../common/util/Functions';
import SendPassordPaSMS from '../SendPassordPaSMS/SendPassordPaSMS';
import { InputContent, ErrorItem, StringObject } from '../../types';
import { useTranslation } from '../../common/util/Hook';
import { StartActivationResult } from '../../generated-types';
/* eslint-disable */
// @ts-ignore
import BankIDavtaleTerm from '../../static/data/BankID-Avtale-Eika-v.2025.07.03.pdf';
import BankIDavtaleTermText from '../../static/data/BankID-Avtale-Eika-v.2025.07.03';
/* eslint-enable */

import './Aktivering.scss';

interface Props {
    config: typeof DefaultConfig;
    useH2?: boolean;
    // eslint-disable-next-line
    submit?: (data: StringObject) => void;
    startOver?:() => void;
    standAlone?: boolean;
    failed?: string;
    activation?: StartActivationResult;
}

interface TextTag {
    tag: string;
    text: string;
}

interface State {
    translated: StringObject,
    id: string;
    formAction: any;
    content: InputContent[];
}

const getTextTag = (text=''): TextTag => {
    let tag = 'div';
    const regH1 = /^#title1/;
    const regH2 = /^#title2/;
    const regH3 = /^#title3/;
    const regH4 = /^#title4/;
    const strong = /^#strong/;
    const point = /^#point/;
    const dash = /^(\s+)?-(\s+)/;

    if ( regH1.test(text) ){
        tag = 'h1';
        text = text.replace( regH1, '' );
    } else if ( regH2.test(text) ){
        tag = 'h2';
        text = text.replace( regH2, '' );
    } else if ( regH3.test(text) ){
        tag = 'h3';
        text = text.replace( regH3, '' );
    } else if ( regH4.test(text) ){
        tag = 'h4';
        text = text.replace( regH4, '' );
    } else if ( strong.test(text) ){
        tag = 'strong';
        text = text.replace( strong, '' );
    } else if ( point.test(text) || dash.test(text) ){
        tag = 'point';
        text = text.replace( point, '' );
    }

    return {tag, text: text.trim()};
};

const getInputContent = (translated: StringObject): InputContent[] => {
    return [
        {
            id: 'password',
            name: 'password',
            label: translated.passord,
            type: Constant.typePassword,
            required: true,
            namespace: 'password',
            fieldButton: {
                type: Constant.typeFieldButtonPasswordReveal,
                action: 'toggle-password-reveal',
                mode: 'hidden',
                textPin: {
                    'hidden': translated.visPassord,
                    'visible': translated.skjulPassord,
                }
            },
            validation: [
                {
                    rule: Constant.validationRequired,
                    message: translated.passordPakrevd,
                }, {
                    rule: Constant.validationPassword,
                    message: translated.passordError,
                }
            ]           
        }, { 
            id: 'agreement',
            name: 'agreement',
            type: Constant.typeElement,
            element: <div className="paragraph-small -only-top">
                <section aria-label={translated.bankIdAvtaleVilkar} className="agreement-info-wrapper">
                    { BankIDavtaleTermText.split("\n").map( (text: string, i: number) => {
                        if ( i === 0 && !text ) { return null; }

                        const key = `term-text-${i}`;
                        const config = getTextTag(text);
                        let output = <p key={key}>{config.text}</p>;
                        if ( config.tag === 'h1' ) {
                            output = <h1 key={key} className="typography-h1">{config.text}</h1>;
                        } else if ( config.tag === 'h2' ) {
                            output = <h2 key={key} className="typography-h2">{config.text}</h2>;
                        } else if ( config.tag === 'h3' ) {
                            output = <h3 key={key} className="typography-h3">{config.text}</h3>;
                        } else if ( config.tag === 'h4' ) {
                            output = <h4 key={key} className="typography-h4">{config.text}</h4>;
                        } else if ( config.tag === 'strong' ) {
                            output = <div key={key} className="strong">{config.text}</div>;
                        } else if ( config.tag === 'point' ) {
                            output = <div key={key} className="point">{config.text}</div>;
                        } else if ( config.text === '' ) {
                            output = <div key={key} className="paragraph-small">{config.text}</div>;
                        }

                        return output;
                    })}
                </section>

                <div className="paragraph-small">
                    <a href={BankIDavtaleTerm} target="blank" download="BankID-Avtale-Eika-v.2025.07.03.pdf" rel="noreferrer" className="bank-id-link agreement-link">{translated.lastNedBankIdAvtaleVilkarPDF}</a>
                </div>
            </div>
        }, {
            id: 'terms',
            name: 'terms',
            legend: 'Bekreftelse',
            type: Constant.typeCheckbox,
            namespace: 'term',
            list: [
                {
                    label: translated.bankIdAvtaleGodtarVilkaar,
                    value: 'yes',
                }
            ],
            validation: [
                {
                    rule: Constant.validationRequired,
                    message: 'Vennligst bekreft akseptering',
                }
            ]
        },
    ];
};


const click = (state: State, setState: (s: State) => void, e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>, key='' ): void => {
    if ( e && typeof(e.preventDefault) === 'function' ) {
        e.preventDefault();
    }

    if ( key === 'toggle-password-reveal' ) {
        const content = getInputContent( state.translated );
        const password = content.find( (data: InputContent) => data.id === 'password');
        if ( !password || !password?.fieldButton ) { return; }

        const current = state.content.find( (data: InputContent) => data.id === 'password');
        if ( !current || !current?.fieldButton ){ return; }

        password.fieldButton.mode = current.fieldButton.mode === 'hidden' ? 'visible' : 'hidden';
        password.type = password.fieldButton.mode === 'hidden' ? Constant.typePassword : Constant.typeText;

        setState({...state, content});
    }
};

const submit = (props: Props, data: StringObject, errorList: ErrorItem[]): void => {
    if ( errorList.length ) { return; }

    if ( props.submit ) { 
        props.submit(data); 
    }
};

const startOver = (props: Props): void => {
    if(props.startOver) {
        props.startOver()
    }
}

export default (props: Props): React.ReactElement => {
    const translated = useTranslation();
    const [state, setState] = useState<State>({
        translated,
        id: generateId('aktivering-mobile'),
        formAction: useForm<any>({mode: 'onBlur'}),
        content: getInputContent( translated ),
    });

    return (
        <div id={state.id} className={classNames({
            'stand-alone-wrapper': !!props.standAlone,
        })}>
            { props.useH2 ?
                <h2 className="paragraph-extra-small">{translated.bankIdAvtaleAktivering}</h2> :
                <h1 className="paragraph-extra-small">{translated.bankIdAvtaleAktivering}</h1>
            }

            <p>{translated.bankIdAvtalePassordBeskrivelse}</p>
            <p>{translated.bankidAvtalePassordRegistrertNummer} <b>{[props.activation?.phoneNumberHint || '*** ** ***']}</b></p>

            <SendPassordPaSMS {...props} namespace="paragraph-large" />

            <FormInput 
                content={state.content} 
                formAction={state.formAction} 
                submitText={translated.aktiver}
                submitCallback={ (data: StringObject, errorList: ErrorItem[], cancel?) => {
                    if(cancel) {
                        startOver(props)
                    } else {
                        submit(props, data, errorList );
                    }
                }}
                failed={props.failed}
                cancelText={translated.startPaaNytt}
                click={(e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>, k='') => { click(state, setState, e, k); }}
            />
        </div>
    );
};
